import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import Modal from "@/components/ui/Modal.jsx";
import Button from "@/components/ui/Button.jsx";
import { Field, Input, PasswordInput, Select } from "@/components/ui/Form.jsx";
import { Callout } from "@/components/ui/Display.jsx";
import { limitLabel, priceLabel } from "@/lib/superAdmin.js";
import { cx } from "@/lib/cx.js";

/*
 * Create an organization AND its first admin, in one form — which is what the
 * endpoint does: superAdminController.createOrganization makes both, and an
 * organization with no admin account cannot be signed into or configured.
 *
 * The identifier is what employees type into the desktop app and cannot be
 * changed afterwards, so it is validated to the same shape the desktop expects
 * rather than left free-form.
 */
const schema = z.object({
    name: z.string().min(2, "At least 2 characters"),
    identifier: z
        .string()
        .min(2, "At least 2 characters")
        .regex(/^[a-z0-9-]+$/, "Lower-case letters, numbers and hyphens only"),
    packageId: z.string().min(1, "Choose a package"),
    adminName: z.string().min(2, "At least 2 characters"),
    email: z.string().email("Enter a valid email"),
    password: z.string().min(8, "At least 8 characters"),
});

export default function OrgFormModal({ packages, saving, error, onSubmit, onClose }) {
    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
    } = useForm({
        resolver: zodResolver(schema),
        defaultValues: { packageId: packages[0]?._id ?? "" },
    });

    return (
        <Modal
            open
            onClose={onClose}
            size="xl"
            title="Create organization"
            subtitle="Creates the organization plus its first admin account"
            footer={
                <>
                    <Button onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        form="orgForm"
                        variant="primary"
                        className="ml-auto"
                        loading={saving}
                        loadingLabel="Creating…"
                        data-create-org-submit
                    >
                        Create organization
                    </Button>
                </>
            }
        >
            <form id="orgForm" noValidate onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                {error && <Callout tone="danger" data-org-form-error>{error}</Callout>}

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field label="Organization name" error={errors.name?.message} htmlFor="oName">
                        <Input
                            id="oName"
                            size="lg"
                            placeholder="Acme Ltd"
                            data-field="name"
                            {...register("name", {
                                // Suggest an identifier from the name, since it
                                // is permanent and easy to get wrong in a hurry.
                                onChange: (e) =>
                                    setValue(
                                        "identifier",
                                        e.target.value
                                            .toLowerCase()
                                            .replace(/[^a-z0-9]+/g, "-")
                                            .replace(/^-|-$/g, ""),
                                        { shouldValidate: true },
                                    ),
                            })}
                        />
                    </Field>

                    <Field
                        label="Identifier"
                        error={errors.identifier?.message}
                        hint="Employees type this into the desktop app. It cannot be changed later."
                        htmlFor="oIdentifier"
                    >
                        <Input
                            id="oIdentifier"
                            size="lg"
                            className="font-mono"
                            placeholder="acme-ltd"
                            autoCapitalize="none"
                            spellCheck={false}
                            data-field="identifier"
                            {...register("identifier")}
                        />
                    </Field>
                </div>

                <Field label="Package" error={errors.packageId?.message} htmlFor="oPackage">
                    <Select id="oPackage" size="lg" data-field="packageId" {...register("packageId")}>
                        {packages.map((p) => (
                            <option key={p._id} value={p._id}>
                                {p.name} — {limitLabel(p.features?.maxUsers)} seats,{" "}
                                {p.features?.dataRetention ?? "—"}d retention · {priceLabel(p)}
                            </option>
                        ))}
                    </Select>
                </Field>

                <div className={cx("rounded-lg border border-slate-200 p-4 dark:border-slate-800")}>
                    <p className="text-[11px] font-semibold tracking-wider text-slate-400 uppercase dark:text-slate-500">
                        First admin account
                    </p>
                    <p className="mt-0.5 mb-3 text-[11.5px] text-slate-400 dark:text-slate-500">
                        Whoever will run this organization. They can add everyone else.
                    </p>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <Field label="Full name" error={errors.adminName?.message} htmlFor="oAdminName">
                            <Input
                                id="oAdminName"
                                size="lg"
                                placeholder="Full name"
                                data-field="adminName"
                                {...register("adminName")}
                            />
                        </Field>

                        <Field label="Email" error={errors.email?.message} htmlFor="oAdminEmail">
                            <Input
                                id="oAdminEmail"
                                size="lg"
                                type="email"
                                placeholder="admin@acme.com"
                                data-field="email"
                                {...register("email")}
                            />
                        </Field>

                        <Field
                            label="Password"
                            error={errors.password?.message}
                            hint="They should change this after signing in."
                            htmlFor="oAdminPassword"
                        >
                            <PasswordInput
                                id="oAdminPassword"
                                size="lg"
                                className="font-mono"
                                placeholder="At least 8 characters"
                                autoComplete="new-password"
                                data-field="password"
                                {...register("password")}
                            />
                        </Field>
                    </div>
                </div>
            </form>
        </Modal>
    );
}
